// The decision page, as a view — and the seam between the two things that
// know about players.
//
// [views.ts](./views.ts) opens by saying what the whole API layer is for: "the
// simulator and the future API both produce these; the client parses them."
// Every screen in the app honors that. The Info Manager did not: it produces a
// `Dossier`, no screen type knew about it, and the one page that reads it
// (`/lab/player/:id`) had to invent its own way of reading a dossier. Two
// producers speaking two languages means every screen gets written twice, and
// the second time is the expensive one.
//
// So this file is the translation. `PlayerDetailView` is the shape a screen
// renders; `playerDetailFromDossier` builds it from the Info Manager, and the
// simulator builds the same thing from its own world. One Zod schema validates
// both, which is what makes "the sim produces the same format" a check rather
// than an intention.
//
// THREE RULES IT INHERITS, all from CLAUDE.md:
//
//   FAIL LOUDLY. A payload that does not parse is dropped and named in
//   `unanswered`, never rendered as a zero or a dash. Absence and zero are
//   different answers and a decision page must not blur them.
//
//   FLAG A SLOT THAT CANNOT SCORE. A bye, a reserve list, a release — these are
//   the one thing the site is required to say out loud. `availability.canScore`
//   is that flag, and it is a fact about eligibility, not a recommendation.
//
//   TOOLS, NOT INSTRUCTIONS. Everything here is a number, a label, or a flag.
//   Nothing in this file composes a sentence about who to play, and nothing
//   ranks two players against each other.
//
// It also never names a sport. What `RES` means is football's business, so the
// caller passes a mapped `DirectoryStatus` in and the sport package stays the
// only place that knows the league's codes.

import { z } from 'zod';
import {
  ContestScheduled,
  ForecastKickoff,
  InjuryReport,
  PersonProfile,
  RosterMembership,
  VenueGridpoint,
  VenueLocation,
  type Dossier,
  type Fact,
} from '@fsp/info';
import { DirectoryStatus } from '../providers/directory';

const Iso = z.string();

// ── The pieces ──────────────────────────────────────────────────────────────

/**
 * Whether this player can score at all this period, and why not.
 *
 * `canScore` is deliberately not derived from the label: a screen must be able
 * to branch on it without matching strings, and the reasons are open-ended
 * (a bye, a reserve list, a release, a week with no fixture loaded).
 */
export const AvailabilityView = z.object({
  canScore: z.boolean(),
  /** 'Active' | 'Practice squad' | 'On a bye' | 'Not on the active roster'. */
  label: z.string(),
  /**
   * The league's own finer code where we hold one, unmapped, shown as-is.
   * `roster.membership.statusDetail` distinguishes injured reserve from PUP
   * from suspended, and nothing has written that code list down yet — so it is
   * carried verbatim rather than guessed at.
   */
  detail: z.string().nullable(),
  /** Why they cannot score, when they cannot. Null when they can. */
  reason: z.string().nullable(),
});
export type AvailabilityView = z.infer<typeof AvailabilityView>;

/**
 * The injury report, always dated.
 *
 * [B-52](../../../../docs/design/BEHAVIORS.md) is explicit that a designation
 * rendered without the day it was filed is wrong — on a Sunday morning an
 * undated "Questionable" reads as current when it is Friday's. `readAt` is
 * therefore not nullable. Note what it means: when WE read the file, which is
 * the honest claim. The source carries no filing date.
 */
export const InjuryReportView = z.object({
  /** 'Questionable' | 'Out'. Null means none has been given — not "healthy". */
  designation: z.string().nullable(),
  /** The body part, as filed. */
  listedWith: z.string().nullable(),
  /** A second one, where the report carries it. */
  alsoListedWith: z.string().nullable(),
  practice: z.string().nullable(),
  readAt: Iso,
});
export type InjuryReportView = z.infer<typeof InjuryReportView>;

/** Somebody else on the squad who is on the report. A name, never a conclusion. */
export const SquadNoteView = z.object({
  playerId: z.string(),
  name: z.string(),
  position: z.string().nullable(),
  listedWith: z.string().nullable(),
  practice: z.string().nullable(),
});
export type SquadNoteView = z.infer<typeof SquadNoteView>;

/**
 * The market, as published, with the ambiguity taken out.
 *
 * A bare spread of `3` does not say who it favors, and a reader has a fifty
 * percent chance of taking it backwards. The sign convention was verified
 * empirically across all 2026 games carrying a line — of 95 with a positive
 * `spreadLine`, 95 had the home moneyline shorter, 0 inconsistent — so a
 * positive number favors the home side. `impliedPoints` is what a serious
 * reader actually wants and is pure arithmetic on two numbers we already hold.
 */
export const MarketView = z.object({
  /** Points, always positive, with `favorite` naming who gets them. */
  spread: z.number().nullable(),
  /** The team code the spread favors. Null when the line is a pick-em or absent. */
  favorite: z.string().nullable(),
  total: z.number().nullable(),
  /** This player's team's share of the total. Null unless both lines are held. */
  impliedPoints: z.number().nullable(),
  /** The opponent's share, for the same reason. */
  opponentImpliedPoints: z.number().nullable(),
});
export type MarketView = z.infer<typeof MarketView>;

/** The fixture, from this player's side of it. */
export const FixtureView = z.object({
  contestId: z.string(),
  opponent: z.string().nullable(),
  home: z.boolean(),
  /** The instant. Null when the schedule carries no clock time for the game. */
  kickoff: Iso.nullable(),
  /** The IANA zone the venue sits in, so a screen can render local time. */
  venueZone: z.string().nullable(),
  venue: z.string().nullable(),
  surface: z.string().nullable(),
  indoors: z.boolean().nullable(),
  /**
   * Days since this side last played. Carried with its norm, because a bare
   * `7` means nothing: 4 is a Thursday game and 14 is off a bye.
   */
  restDays: z.number().int().nullable(),
  market: MarketView.nullable(),
});
export type FixtureView = z.infer<typeof FixtureView>;

/**
 * The forecast at kickoff. A forecast is what we believed then, so it is dated
 * for the same reason the injury report is.
 */
export const ConditionsView = z.object({
  sky: z.string().nullable(),
  temperatureF: z.number().nullable(),
  windMph: z.number().nullable(),
  windDirection: z.string().nullable(),
  precipitationChance: z.number().nullable(),
  issuedAt: Iso,
});
export type ConditionsView = z.infer<typeof ConditionsView>;

/** Who said so, rendered verbatim. One line per distinct set of terms. */
export const ProvenanceView = z.object({
  sourceId: z.string(),
  name: z.string(),
  license: z.string(),
  text: z.string(),
  url: z.string().nullable(),
});
export type ProvenanceView = z.infer<typeof ProvenanceView>;

/**
 * A question this page did not answer, and why.
 *
 * Carried into the view rather than dropped, because "we have no news source
 * whose terms permit this" and "this player has no news" are different answers
 * and a screen that shows an empty list for both is lying about one of them.
 */
export const UnansweredView = z.object({
  topic: z.string(),
  reason: z.string(),
});
export type UnansweredView = z.infer<typeof UnansweredView>;

/** Biography. Real, and deliberately last: none of it changes a start or a sit. */
export const PlayerBioView = z.object({
  jerseyNumber: z.number().int().nullable(),
  yearsExperience: z.number().int().nullable(),
  ageYears: z.number().int().nullable(),
  heightInches: z.number().int().nullable(),
  weightPounds: z.number().int().nullable(),
  college: z.string().nullable(),
  draft: z
    .object({
      year: z.number().int(),
      round: z.number().int().nullable(),
      pick: z.number().int().nullable(),
      team: z.string().nullable(),
    })
    .nullable(),
});
export type PlayerBioView = z.infer<typeof PlayerBioView>;

// ── The view ────────────────────────────────────────────────────────────────

/**
 * Everything known about one player for one period, in the app's vocabulary.
 *
 * Scenario E2 — "open a player card from anywhere and decide". The card that
 * exists today is `PlayerCardData`, an ad-hoc interface declared inside a
 * component file, carrying five fields. This is what that becomes once there is
 * real data behind it.
 *
 * Every section is nullable and every absence has a matching entry in
 * `unanswered`. A screen renders what is here and says why the rest is not.
 */
export const PlayerDetailView = z.object({
  player: z.lazy(() => PlayerSummaryRef),
  /** The period this was assembled for, which is not always the period the data is from. */
  season: z.number().int(),
  period: z.number().int(),
  availability: AvailabilityView,
  bio: PlayerBioView,
  injury: InjuryReportView.nullable(),
  fixture: FixtureView.nullable(),
  conditions: ConditionsView.nullable(),
  /** Teammates on the injury report. Never the whole squad. */
  around: z.array(SquadNoteView),
  provenance: z.array(ProvenanceView),
  unanswered: z.array(UnansweredView),
  /** What we knew at this instant. Echoed so a caller can cache against it. */
  asOf: Iso,
});
export type PlayerDetailView = z.infer<typeof PlayerDetailView>;

/**
 * The identity block, kept structurally identical to `PlayerSummary` in
 * views.ts so a detail view can be collapsed into a row without a second
 * lookup. Declared here rather than imported to keep this file's dependency
 * one-directional; `playerSummaryOf` below is the bridge.
 */
const PlayerSummaryRef = z.object({
  id: z.string(),
  name: z.string(),
  shortName: z.string(),
  positions: z.array(z.string()),
  proTeam: z.string().nullable(),
  injury: z
    .object({
      status: z.string(),
      name: z.string(),
      severity: z.enum(['none', 'warn', 'likely-out', 'out']),
      note: z.string().nullable(),
    })
    .nullable(),
  onBye: z.boolean(),
});

// ── Building it from the Info Manager ───────────────────────────────────────

export interface PlayerDetailOptions {
  /**
   * The sport's reading of a raw roster status. `@fsp/domain` must never know
   * what `RES` means — see CLAUDE.md, "the core never names a sport" — so the
   * caller passes the sport package's mapping in.
   */
  mapStatus: (raw: string | null) => z.infer<typeof DirectoryStatus>;
  /** For the age calculation. Defaults to the dossier's own `asOf`. */
  now?: string;
}

/** A fact whose subject is the dossier's own entity. Never a teammate's. */
function ownFact(d: Dossier, kind: string): Fact | undefined {
  return Object.values(d.sections)
    .flatMap((s) => s.facts)
    .find(
      (f) =>
        f.kind === kind && f.refs.some((r) => r.role === 'subject' && r.entityId === d.entity.id),
    );
}

/** A fact from one named section, about his week rather than about him. */
function factIn(d: Dossier, section: 'contest' | 'environment', kind: string): Fact | undefined {
  return d.sections[section]?.facts.find((f) => f.kind === kind);
}

/**
 * Parse a payload with the registry's own schema — the same one the store
 * validated it against on the way in.
 *
 * A MISMATCH IS NOT AN ABSENCE, and this is the whole reason the reader takes a
 * collector. Returning `undefined` for both meant a malformed roster row
 * rendered as "Not on a roster": a confident, wrong, entirely plausible answer
 * about a player who is on the roster. It cost an hour to find in a simulated
 * world where the payload was simply missing keys, and it would have cost far
 * more in production where the payload is somebody else's and changes without
 * warning.
 *
 * So a fact we cannot read says so out loud, in the one place a screen already
 * renders reasons.
 */
function payloadReader(unanswered: UnansweredView[]) {
  return function read<T extends z.ZodTypeAny>(
    schema: T,
    fact: Fact | undefined,
    kind: string,
  ): z.infer<T> | undefined {
    if (fact === undefined) return undefined;
    const r = schema.safeParse(fact.payload);
    if (r.success) return r.data as z.infer<T>;
    const issue = r.error.issues[0];
    unanswered.push({
      topic: kind,
      reason:
        `the store returned a ${kind} this app could not read` +
        (issue === undefined ? '' : ` (${issue.path.join('.') || 'payload'}: ${issue.message})`),
    });
    return undefined;
  };
}

function ageFrom(birthDate: string | null | undefined, at: string): number | null {
  if (birthDate === null || birthDate === undefined) return null;
  const born = Date.parse(birthDate);
  const now = Date.parse(at);
  if (Number.isNaN(born) || Number.isNaN(now)) return null;
  return Math.floor((now - born) / (365.2425 * 24 * 60 * 60 * 1000));
}

/**
 * The implied points each side is expected to score.
 *
 * total = home + away, and spread is how many points the home side gives, so
 * home = (total + spread) / 2. Arithmetic on two published numbers, which is
 * why it is a number on the page and never a verdict about it.
 */
export function impliedPoints(total: number, spread: number): { home: number; away: number } {
  const home = (total + spread) / 2;
  return { home, away: total - home };
}

export function playerDetailFromDossier(d: Dossier, opts: PlayerDetailOptions): PlayerDetailView {
  const now = opts.now ?? d.asOf;
  const unanswered: UnansweredView[] = d.incomplete.map((i) => ({
    topic: i.section,
    reason: i.detail,
  }));

  const read = payloadReader(unanswered);
  const profile = read(PersonProfile, ownFact(d, 'person.profile'), 'person.profile');
  const membership = read(RosterMembership, ownFact(d, 'roster.membership'), 'roster.membership');
  const injuryFact = ownFact(d, 'injury.report');
  const injury = read(InjuryReport, injuryFact, 'injury.report');
  const contest = read(
    ContestScheduled,
    factIn(d, 'contest', 'contest.scheduled'),
    'contest.scheduled',
  );
  const forecastFact = factIn(d, 'environment', 'forecast.kickoff');
  const forecast = read(ForecastKickoff, forecastFact, 'forecast.kickoff');
  const grid = read(VenueGridpoint, factIn(d, 'environment', 'venue.gridpoint'), 'venue.gridpoint');
  // Our curated venue name, not the fixture's: a schedule file carries the name
  // a venue had in the season of each row, and sponsors change.
  const venue = read(VenueLocation, factIn(d, 'environment', 'venue.location'), 'venue.location');

  // `roster.membership` is the period-scoped claim and `person.profile` is the
  // career-latest one. Prefer the first; fall back rather than render no team
  // at all, which is how the page looked on any week the roster file had not
  // been published for.
  const team = membership?.team ?? profile?.latestTeam ?? null;

  // The reason CODE, never the English. `detail` is written for a person and
  // the two stores word it differently; `reason` is what the contract promises.
  const onBye = d.incomplete.some((i) => i.section === 'contest' && i.reason === 'bye');
  const availability = availabilityOf(membership, opts.mapStatus, onBye);

  const atHome = contest != null && team != null && contest.home === team;
  const opponent = contest == null || team == null ? null : atHome ? contest.away : contest.home;

  return {
    player: {
      id: d.entity.id,
      name: profile?.displayName ?? d.entity.display,
      shortName: profile?.shortName ?? profile?.displayName ?? d.entity.display,
      positions: profile?.position == null ? [] : [profile.position],
      proTeam: team,
      injury:
        injury == null
          ? null
          : {
              status: injury.reportStatus ?? injury.practiceStatus ?? 'On the report',
              name: injury.reportInjury ?? injury.practiceInjury ?? 'Unspecified',
              severity: severityOf(injury.reportStatus),
              note: null,
            },
      onBye,
    },
    season: d.season,
    period: d.period,
    availability,
    bio: {
      jerseyNumber: membership?.jerseyNumber ?? null,
      yearsExperience: membership?.yearsExperience ?? null,
      ageYears: ageFrom(profile?.birthDate, now),
      heightInches: profile?.heightInches ?? null,
      weightPounds: profile?.weightPounds ?? null,
      college: profile?.college ?? null,
      draft:
        profile?.draftYear == null
          ? null
          : {
              year: profile.draftYear,
              round: profile.draftRound ?? null,
              pick: profile.draftPick ?? null,
              team: profile.draftTeam ?? null,
            },
    },
    injury:
      injury == null || injuryFact === undefined
        ? null
        : {
            designation: injury.reportStatus ?? null,
            listedWith: injury.reportInjury ?? injury.practiceInjury ?? null,
            alsoListedWith: injury.practiceSecondaryInjury ?? null,
            practice: injury.practiceStatus ?? null,
            readAt: injuryFact.observedAt,
          },
    fixture:
      contest == null
        ? null
        : {
            contestId: contest.gameId,
            opponent,
            home: atHome,
            kickoff: contest.kickoff ?? null,
            venueZone: grid?.timeZone ?? null,
            venue: venue?.name ?? null,
            surface: contest.surface ?? null,
            indoors: roofIsIndoors(contest.roof),
            restDays: (atHome ? contest.homeRest : contest.awayRest) ?? null,
            market: marketOf(contest, team, atHome),
          },
    conditions:
      forecast == null
        ? null
        : {
            sky: forecast.shortForecast ?? null,
            temperatureF: forecast.temperatureF ?? null,
            windMph: forecast.windMph ?? null,
            windDirection: forecast.windDirection ?? null,
            precipitationChance: forecast.precipitationChance ?? null,
            issuedAt: forecast.issuedAt,
          },
    around: squadNotes(d, read),
    provenance: d.attribution.map((a) => ({
      sourceId: a.sourceId,
      name: a.name,
      license: a.license,
      // Nullable upstream: a source may carry a license with no required
      // wording. The screen needs something to render, so name the license.
      text: a.text ?? `${a.name} — ${a.license}`,
      url: a.url ?? null,
    })),
    unanswered,
    asOf: d.asOf,
  };
}

function roofIsIndoors(roof: string | null | undefined): boolean | null {
  if (roof == null) return null;
  return roof === 'dome' || roof === 'closed';
}

function severityOf(
  reportStatus: string | null | undefined,
): 'none' | 'warn' | 'likely-out' | 'out' {
  switch (reportStatus) {
    case 'Out':
      return 'out';
    case 'Doubtful':
      return 'likely-out';
    case 'Questionable':
      return 'warn';
    default:
      return 'none';
  }
}

function availabilityOf(
  membership: z.infer<typeof RosterMembership> | undefined,
  mapStatus: PlayerDetailOptions['mapStatus'],
  onBye: boolean,
): AvailabilityView {
  const detail = membership?.statusDetail ?? null;

  // A bye outranks everything else: an active player on a team with no fixture
  // still cannot score, and that is the flag CLAUDE.md requires.
  if (onBye) {
    return {
      canScore: false,
      label: 'On a bye',
      detail,
      reason: 'their team has no fixture this week',
    };
  }
  if (membership === undefined) {
    return {
      canScore: false,
      label: 'Not on a roster',
      detail,
      reason: 'we hold no roster membership for them at or before this week',
    };
  }
  switch (mapStatus(membership.status ?? null)) {
    case 'active':
      return { canScore: true, label: 'Active', detail, reason: null };
    case 'practice-squad':
      // Not an out. A practice-squad player can be elevated on Saturday and
      // score on Sunday, so this is a warning and the flag stays honest.
      return {
        canScore: false,
        label: 'Practice squad',
        detail,
        reason: 'can be elevated before the game, but is not on the active roster now',
      };
    case 'retired':
      return { canScore: false, label: 'Retired', detail, reason: 'no longer playing' };
    default:
      return {
        canScore: false,
        label: 'Not on the active roster',
        detail,
        reason: 'on a reserve, exempt or released list',
      };
  }
}

function marketOf(
  contest: z.infer<typeof ContestScheduled>,
  team: string | null,
  atHome: boolean,
): MarketView | null {
  const spread = contest.spreadLine ?? null;
  const total = contest.totalLine ?? null;
  if (spread === null && total === null) return null;

  const favorite =
    spread === null || spread === 0 ? null : spread > 0 ? contest.home : contest.away;

  let mine: number | null = null;
  let theirs: number | null = null;
  if (spread !== null && total !== null && team !== null) {
    const { home, away } = impliedPoints(total, spread);
    mine = atHome ? home : away;
    theirs = atHome ? away : home;
  }

  return {
    spread: spread === null ? null : Math.abs(spread),
    favorite,
    total,
    impliedPoints: mine,
    opponentImpliedPoints: theirs,
  };
}

/**
 * Teammates on the injury report, with their names attached.
 *
 * `SectionResult.entities` carries the entities the facts' refs name, which is
 * the whole reason a fact travels with its edges — a caller holding a squad's
 * reports should never have to read a vendor key out of a payload and know
 * which namespace it belongs to.
 */
function squadNotes(d: Dossier, read: ReturnType<typeof payloadReader>): SquadNoteView[] {
  const section = d.sections.teammates;
  if (section === undefined) return [];
  const names = new Map(section.entities.map((e) => [e.id, e.display]));

  const out: SquadNoteView[] = [];
  for (const f of section.facts) {
    if (f.kind !== 'injury.report') continue;
    const subject = f.refs.find((r) => r.role === 'subject');
    if (subject === undefined || subject.entityId === d.entity.id) continue;
    const payload = read(InjuryReport, f, 'injury.report');
    if (payload === undefined) continue;
    out.push({
      playerId: subject.entityId,
      name: names.get(subject.entityId) ?? 'Unnamed teammate',
      position: payload.position ?? null,
      listedWith: payload.reportInjury ?? payload.practiceInjury ?? null,
      practice: payload.practiceStatus ?? null,
    });
  }
  return out;
}
